import PropTypes from "prop-types";
import Button, {
  buttonVariants,
} from "@visiblethread/react-components/dist/components/Button";
import { saveButtonError } from "constants/dictionary";
import { Link } from "react-router-dom";
import React, { useEffect } from "react";
import Dropdown, {
  dropdownTypes,
} from "@visiblethread/react-components/dist/components/Dropdown";
import ButtonsDropdownOption from "@visiblethread/react-components/dist/components/Dropdown/ButtonsDropdownOption";
import { ReactComponent as PenIcon } from "icons/pen-icon.svg";
import { ReactComponent as EyeIcon } from "icons/eye.svg";
import { ReactComponent as MenuIcon } from "icons/threeDots.svg";
import { hideInlineAlert, showInlineAlert } from "slices/app/appSlice";
import { alertVariants } from "@visiblethread/react-components/dist/components/Alert";
import { useDispatch, useSelector } from "react-redux";
import {
  closeEditMode,
  exportReadabilityIgnoreList,
  importReadabilityIgnoreList,
  openEditMode,
  setReadabilityIgnoreList,
} from "slices/settings/actions/readabilityIgnoreList";
import { showModal } from "slices/app/actions/modals";
import { modalTypes } from "constants/types";
import { selectReadabilityIgnore } from "slices/settings/selectors";
import { TooltipTrigger } from "@visiblethread/react-components/dist/components/Tooltip";

const tooltipText =
  "This is a list of words and phrases that we will ignore when running readability.";

const Header = ({ hasErrors }) => {
  const dispatch = useDispatch();

  const { editMode, hasBeenEdited, locked, updatedBy, ignoreList } =
    useSelector(selectReadabilityIgnore);
  // eslint-disable-next-line no-undef
  const { roles, userName } = UserInfo;
  const lockedByOtherUser = locked && updatedBy !== userName;
  const hasPermissions = roles.split(",").includes("readability_settings");

  useEffect(() => {
    if (!hasPermissions) {
      dispatch(
        showInlineAlert({
          variant: alertVariants.warning,
          text: "You do not have permission to edit Readability Ignore List",
        })
      );
    } else if (locked && lockedByOtherUser) {
      dispatch(
        showInlineAlert({
          variant: alertVariants.warning,
          text: `You can not edit this list as ${updatedBy} is currently editing it`,
        })
      );
    }

    return () => {
      dispatch(hideInlineAlert());
    };
  }, [dispatch, hasPermissions, locked, updatedBy]);
  const handleSaveClick = () => {
    dispatch(setReadabilityIgnoreList(ignoreList));
  };
  const handleCancelClick = () => {
    dispatch(closeEditMode());
  };
  const handleEditClick = () => {
    dispatch(openEditMode());
  };
  const handleExportToCSV = () => {
    dispatch(exportReadabilityIgnoreList());
  };
  const handleImportFromCSV = () => {
    const onOk = (formData) => dispatch(importReadabilityIgnoreList(formData));
    dispatch(
      showModal(modalTypes.IMPORT_FILE, {
        title: "Import Readability Ignore List",
        accepts: ".csv",
        onOk,
        okText: "Import",
      })
    );
  };

  const menuOptions = [
    {
      content: "Export to CSV",
      onClick: handleExportToCSV,
    },
    {
      content: "Import from CSV",
      onClick: handleImportFromCSV,
    },
  ];
  const threeDots = () => (
    <Dropdown
      type={dropdownTypes.buttons}
      propsObject={{
        hideArrowIcon: true,
        button: (
          <span>
            <MenuIcon />
          </span>
        ),
        options: menuOptions.map(({ content, onClick, dataTest }) => (
          <ButtonsDropdownOption
            key={content}
            content={content}
            onClick={onClick}
            disabled={!hasPermissions || locked}
            data-test={dataTest}
          />
        )),
        buttonVariant: buttonVariants.secondary,
      }}
    />
  );
  return (
    <div
      className="readability_ignore_list__header"
      data-testid="readability_ignore_list__header"
    >
      <div className="readability_ignore_list__header-left">
        <h1>Readability Ignore List</h1>

        <TooltipTrigger content={tooltipText}>
          <div
            className="readability_ignore_list__header-left__info_icon"
            aria-label="information tooltip icon"
          >
            i
          </div>
        </TooltipTrigger>
        {editMode ? (
          <div className="readability_ignore_list__header-left__info">
            <PenIcon width={10} height={10} />
            <span>You are editing this Ignore list</span>
          </div>
        ) : (
          <div className="readability_ignore_list__header-left__info">
            <EyeIcon width={14} height={14} />
            <span>You are viewing this</span>
          </div>
        )}
      </div>
      <div className="readability_ignore_list__header-right">
        {editMode ? (
          <>
            <Button
              content="Save edits"
              variant={buttonVariants.primary}
              disabled={!hasBeenEdited || hasErrors}
              onClick={handleSaveClick}
              error={hasErrors ? saveButtonError : ""}
            />
            <Link
              to="/readability"
              className="secondary_link"
              onClick={handleCancelClick}
            >
              Cancel Edit
            </Link>
          </>
        ) : (
          <Button
            content="Edit Ignore List"
            variant={buttonVariants.primary}
            disabled={!hasPermissions || lockedByOtherUser}
            onClick={handleEditClick}
          />
        )}
        {!editMode && threeDots()}
      </div>
    </div>
  );
};

export default Header;

Header.propTypes = {
  hasErrors: PropTypes.bool.isRequired,
};
